'use client';

import './geo-banner.scss';

import { deleteCookie, getCookie } from 'cookies-next';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Intl } from '@sola/ark-core/atoms/intl/Intl.js';
import {
  Prominence,
  Sizes,
  Text,
  Variants,
} from '@sola/ark-core/atoms/text/text.js';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.js';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.js';
import { setCICookie } from '@sola/tracking/lib/cookies.js';

import { FeatureBase } from '../../molecules/feature-base/feature-base';
import { FlexibleSection } from '../flexible-section/flexible-section';

export type GeolocationBannerDetailsProps = {
  countryName: string;
  country: string;
  url: string;
};

enum CountryCodes {
  ca = 'ca',
}

type GeolocationRedirectDetails = {
  [key in CountryCodes]: GeolocationBannerDetailsProps;
};

const CountryDetails: GeolocationRedirectDetails = {
  [CountryCodes.ca]: {
    country: 'Canadian',
    countryName: 'Canada',
    url: 'https://www.compassion.ca/',
  },
};

const radioValues = {
  yes: 'Yes',
  no: 'No',
};

const geoCookieName = 'ignoreGeoRedirect';
const dontAskAgainCookie = getCookie(geoCookieName);

const InnerGeoBanner = ({
  countryCode,
  onClose,
}: {
  countryCode?: CountryCodes;
  onClose: () => void;
}) => {
  const router = useRouter();
  const { t } = useTranslation();

  const handleDontAskToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setCICookie(new URLSearchParams(`${geoCookieName}=true`), [
        geoCookieName,
      ]);
    } else {
      deleteCookie(geoCookieName);
    }
  };

  if (!countryCode || !CountryDetails[countryCode as CountryCodes]) {
    return null;
  }

  return (
    <FeatureBase
      className="GeoBanner"
      body={
        <div className="GeoBanner__content">
          <div className="GeoBanner__radioGroup">
            <Text
              variant={Variants.Label}
              prominence={Prominence.Potent}
              size={Sizes.XLarge}
            >
              <Intl
                k="geo.redirectBanner.title"
                variables={{
                  countryName:
                    CountryDetails[countryCode as CountryCodes]?.countryName,
                  country: CountryDetails[countryCode as CountryCodes]?.country,
                }}
              />
            </Text>
            <RadioGroup
              name="geo-redirect"
              variant="buttons"
              onChange={(v: React.ChangeEvent<HTMLInputElement>) => {
                if (v.target.value === radioValues.no) {
                  onClose();
                }
                if (v.target.value === radioValues.yes) {
                  try {
                    router.push(
                      CountryDetails[countryCode as CountryCodes]?.url,
                    );
                  } catch (error) {
                    console.error('Geo redirect error:', error);
                  }
                }
              }}
              items={[
                {
                  value: radioValues.yes,
                  text: t('core.actions.yes'),
                },
                { value: radioValues.no, text: t('core.actions.no') },
              ]}
            />
          </div>
          <div className="GeoBanner__footer">
            <Checkbox
              label={t('geo.redirectBanner.dontAskAgain')}
              name="geoRedirectDontAskAgain"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleDontAskToggle(e)
              }
            />
            <Text
              size={Sizes.Medium}
              prominence={Prominence.Potent}
              variant={Variants.Label}
            >
              <Intl
                k="geo.redirectBanner.note"
                variables={{
                  country: CountryDetails[countryCode as CountryCodes]?.country,
                }}
              />
            </Text>
          </div>
        </div>
      }
    />
  );
};

export const GeoBanner = () => {
  const [open, setOpen] = useState(false);
  const [geo, setGeo] = useState<string>();
  const searchParams = useSearchParams();

  useEffect(() => {
    const countryCode = searchParams.get('geo');
    if (countryCode && !dontAskAgainCookie) {
      setGeo(countryCode);
      setOpen(true);
    }
  }, []);

  if (!open) {
    return null;
  }

  return (
    <FlexibleSection
      meta={{
        layoutRatio: '1-1',
        theme: 'darkBlue',
        fullWidth: true,
      }}
      children1={
        <InnerGeoBanner
          countryCode={geo as CountryCodes}
          onClose={() => setOpen(false)}
        />
      }
    />
  );
};
